/**
 * Which module a nav link's page belongs to — for the small lock the sidebars
 * draw beside a row the org has not bought.
 *
 * The page's own gate is its route's requiredModule / requiredAnyModule in
 * routes/index.jsx; this table mirrors it by path prefix so a row can say
 * "locked" before it is clicked. navModules.test.js checks every gated
 * top-level route in routes/index.jsx against it, so the two cannot drift.
 *
 * First matching prefix wins (longest first within a family). A value is a
 * slug, a list of slugs (any one licenses the page), or null (core / not
 * module-gated: never locked).
 */
const ENDPOINT_PREFIXES = [
  '/endpoint-security', '/admin/endpoint-settings', '/admin/endpoint-violations',
  '/admin/fleet-overview', '/admin/shadow-ai-discovery', '/admin/ai-conversations',
  '/admin/dlp-analytics', '/admin/endpoint-user-activity', '/admin/ai-tool-catalog',
];

const CUSTOM_AGENTS_TELEMETRY = 'monitoring';
const CUSTOM_AGENTS_EVAL_TOOLS = ['monitoring', 'governance'];

// Tenants buy the five modules of the pillar navigation (saf3ai-auth-sdk
// modules.yaml v2); a page locks on the module of the pillar it sits in.
// Data sources (Copilot, Gemini, the AI providers, Custom Agents, AI Gateway,
// MDM, EDR) are core: their paths still name their slug, which never locks.
const DISCOVERY = 'discovery';
const ASSESSMENT = 'assessment';
const SECURITY = 'security';
const FINOPS = 'finops';
const GOVERNANCE = 'governance';

export const NAV_PATH_MODULES = [
  // Agent Atlas: Home, Inbox, Log Explorer, Notifications and Policy are core;
  // the pillar pages lock on their module.
  ['/agent-atlas/inventory', DISCOVERY],
  // The Users list. A user's own page (/agent-atlas/users/:email) is reached
  // from every pillar, so its route stays ungated; no nav row links to it.
  ['/agent-atlas/users', DISCOVERY],
  ['/agent-atlas/assessment', ASSESSMENT],
  ['/agent-atlas/finops', FINOPS],
  ['/agent-atlas/adoption', FINOPS],
  ['/agent-atlas/compliance-audit', GOVERNANCE],
  ['/agent-atlas/compliance', GOVERNANCE],
  ['/agent-atlas', null],
  // Security (Atlas). AI-BOM sits in the Discovery pillar.
  ['/security-atlas/ai-bom', DISCOVERY],
  ['/security-atlas', SECURITY],
  // Custom Agents (the `monitoring` source). Pinned Atlas pages lock on the
  // pillar module they belong to.
  ['/custom-agents/assessment', ASSESSMENT],
  ['/custom-agents/finops', FINOPS],
  ['/custom-agents/compliance', GOVERNANCE],
  ['/custom-agents/alerts', 'monitoring'],
  ['/custom-agents/recommendations', ['monitoring', SECURITY]],
  ['/custom-agents/registry', null],
  ['/custom-agents/audit-log', null],
  ['/custom-agents/policy', null],
  ['/custom-agents/settings', null],
  ['/custom-agents', CUSTOM_AGENTS_TELEMETRY],
  ['/observability/alerts', 'monitoring'],
  ['/observability', CUSTOM_AGENTS_TELEMETRY],
  ['/agent-network', CUSTOM_AGENTS_TELEMETRY],
  ['/dashboard', CUSTOM_AGENTS_EVAL_TOOLS],
  ['/v2/insights/roi', FINOPS],
  ['/v2/posture/compliance', GOVERNANCE],
  ['/v2/posture/audit-report', GOVERNANCE],
  ['/user-behavior', CUSTOM_AGENTS_TELEMETRY],
  ['/intent-analysis', CUSTOM_AGENTS_TELEMETRY],
  ['/global-search', CUSTOM_AGENTS_TELEMETRY],
  ['/conversation-explorer', CUSTOM_AGENTS_TELEMETRY],
  ['/red-team-scanner', ASSESSMENT],
  ['/red-team', ASSESSMENT],
  ['/model-evaluation', ASSESSMENT],
  ['/ai-explorer', CUSTOM_AGENTS_EVAL_TOOLS],
  ['/finops/usage-roi', FINOPS],
  ['/admin/compliance', GOVERNANCE],
  ['/admin/custom-guardrails', SECURITY],
  ['/security/audit-log', null],
  ['/security', SECURITY],
  ['/reports', SECURITY],
  // Endpoints and Cloud are Discovery pages; the other sources are core.
  ['/cloud', DISCOVERY],
  ['/gemini/agents', 'gemini_enterprise'],
  ['/gemini', 'gemini'],
  ['/copilot/studio', 'copilot_studio'],
  ['/copilot', 'copilot'],
  ['/sast', ASSESSMENT],
  ['/admin/ai-gateway', 'ai_gateway'],
  ['/admin/mdm', 'mdm'],
  ['/admin/edr', 'edr'],
  ...ENDPOINT_PREFIXES.map((p) => [p, DISCOVERY]),
];

const matches = (pathname, prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`);

/**
 * The module(s) gating a nav target, or null. `/saas-ai/*` answers with the
 * ?vendor= the link carries (each AI provider is its own module) and null
 * without one — the page then locks on whichever vendor is active.
 */
export function moduleForNavPath(to) {
  if (!to) return null;
  const raw = typeof to === 'string' ? to : `${to.pathname || ''}${to.search || ''}`;
  const [pathname, query = ''] = raw.split('?');
  if (matches(pathname, '/saas-ai')) {
    const vendor = new URLSearchParams(query).get('vendor');
    return vendor || null;
  }
  const hit = NAV_PATH_MODULES.find(([prefix]) => matches(pathname, prefix));
  return hit ? hit[1] : null;
}

/** True when the org may NOT open `to` (its module is not licensed). */
export function isNavPathLocked(to, license) {
  if (!license || !license.enforcing) return false;
  const mod = moduleForNavPath(to);
  if (!mod) return false;
  return Array.isArray(mod) ? !license.isAnyLicensed(mod) : !license.isLicensed(mod);
}
